import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export type ModalSize = "sm" | "md" | "lg" | "xl";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEsc?: boolean;
  preventBodyScroll?: boolean;
  icon?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "cht-max-w-md",   // ~448px: Confirmations, alerts, compact prompts
  md: "cht-max-w-lg",   // ~512px: Standard dialogs, standard forms
  lg: "cht-max-w-2xl",  // ~672px: Rich forms, multi-input records
  xl: "cht-max-w-4xl",  // ~896px: Wide media viewers, dashboards, dense tables
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = "md",
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEsc = true,
  preventBodyScroll = true,
  icon,
  footer,
  className = "",
  headerClassName = "",
  bodyClassName = "",
}) => {
  // Prevent background page scrolling while modal is open
  useEffect(() => {
    if (!preventBodyScroll || !isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, preventBodyScroll]);

  // Handle Escape key press
  useEffect(() => {
    if (!closeOnEsc || !isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeOnEsc, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4 sm:cht-p-6"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="cht-absolute cht-inset-0 cht-bg-slate-900/40 cht-backdrop-blur-sm cursor-pointer"
            onClick={closeOnBackdropClick ? onClose : undefined}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`
              cht-relative cht-w-full ${sizeClasses[size]} cht-bg-white
              cht-rounded-[32px] cht-p-6 sm:cht-p-8
              cht-shadow-[0_20px_60px_rgba(0,0,0,0.12)]
              cht-max-h-[90vh] cht-overflow-y-auto
              cht-border cht-border-gray-100
              ${className}
            `}
          >
            {/* Header */}
            {(title || showCloseButton || icon) && (
              <div
                className={`cht-flex cht-items-start cht-justify-between cht-gap-4 cht-mb-6 ${headerClassName}`}
              >
                <div className="cht-flex cht-items-center cht-gap-3">
                  {icon && (
                    <div className="cht-p-2.5 cht-rounded-2xl cht-bg-[#f2f4fd] cht-text-[#3e57da] cht-shrink-0">
                      {icon}
                    </div>
                  )}
                  <div>
                    {title && (
                      <h2 className="cht-text-2xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
                        {title}
                      </h2>
                    )}
                    {subtitle && (
                      <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-0.5">
                        {subtitle}
                      </p>
                    )}
                  </div>
                </div>

                {showCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close modal"
                    className="cht-p-2 cht-bg-gray-50 cht-rounded-xl cht-text-gray-400 hover:cht-text-gray-700 hover:cht-bg-gray-100 cht-transition-all cht-shrink-0 cursor-pointer"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}

            {/* Body Content */}
            <div className={bodyClassName}>{children}</div>

            {/* Optional Footer */}
            {footer && (
              <div className="cht-mt-6 cht-pt-4 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-end cht-gap-3">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;
